import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Button, Form, Spinner } from "react-bootstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useFinanceApi from "../../services/financeApi";
import { toast } from "react-toastify";
import { GetCurrentUserName } from "../../utils/permissionChecker";
const AddExpense = ({ closeModal }) => {
  const { t } = useTranslation();
  const loggedInUser = GetCurrentUserName();
  const {
    addExpense,
    get_expense_catagory,
    get_payment_method,
  } = useFinanceApi();
  const queryClient = useQueryClient();
  const { mutate: RegisterExpenseMutation } = useMutation(addExpense);
  const getExpenseCategoryQuery = useQuery(["expense_categories"], () =>
    get_expense_catagory()
  );
  const getPaymentMethodQuery = useQuery(["get_payment_method"], () =>
    get_payment_method()
  );

  if (getExpenseCategoryQuery.isLoading || getPaymentMethodQuery.isLoading) {
    return <div>Loading...</div>;
  }

  if (getExpenseCategoryQuery.isError) {
    return <div>Error: {getExpenseCategoryQuery.error.message}</div>;
  }
  if (getPaymentMethodQuery.isError) {
    return <div>Error: {getPaymentMethodQuery.error.message}</div>;
  }
  const categories = getExpenseCategoryQuery.data.data.expense_catagory;
  const getPayment = getPaymentMethodQuery.data.data.Payment;
  const initialValues = {
    ex_date: "",
    expense_for: "",
    amount: "",
    catagory_id: "",
    payment_reference: "",
    payment_type: "",
    expense_description: "",
    added_by: loggedInUser,
    updated_by: loggedInUser,
  };
  const validationSchema = Yup.object().shape({
    ex_date: Yup.string().trim().required("Required"),
    expense_for: Yup.string().required("Required"),
    amount: Yup.number() // Ensure amount is a number
      .typeError("Amount must be a number") // Custom error message for non-number input
      .required("Required")
      .positive("Amount must be a positive number"),
    catagory_id: Yup.string().required("Required"),
    payment_reference: Yup.string().trim().required("Required"),
    payment_type: Yup.string().required("Required"),
  });

  const onSubmit = async (values) => {
    try {
      RegisterExpenseMutation(values, {
        onSuccess: () => {
          closeModal();
          queryClient.invalidateQueries("expenseByDate");
          toast.success("Expense is registered successfully");
        },
        onError: (error) => {
          toast.error(`Failed register Expense: ${error?.message}`);
        },
      });
    } catch (error) {
      toast.error(error.message, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };
  return (
    <Container fluid>
      <Row>
        <Col lg="12">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group controlId="ex_date">
                  <Form.Label>Expense Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="ex_date"
                    value={values.ex_date}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.ex_date && !!errors.ex_date}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ex_date}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group>
                  <Form.Label className="text-secondary">
                    Expense For
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="expense_for"
                    value={values.expense_for}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.expense_for && !!errors.expense_for}
                    placeholder="Expense For"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.expense_for}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId="amount">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="text"
                    name="amount"
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.amount && !!errors.amount}
                    placeholder="Amount"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amount}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="catagory_id">
                  <Form.Label>Expense Type</Form.Label>
                  <Form.Control
                    as="select"
                    name="catagory_id"
                    value={values.catagory_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.catagory_id && !!errors.catagory_id}
                  >
                    <option value="">Select a expense type</option>
                    {categories &&
                      categories.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {errors.catagory_id}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="payment_type">
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Control
                    as="select"
                    name="payment_type"
                    value={values.payment_type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.payment_type && !!errors.payment_type}
                  >
                    <option value="">Select a payment method</option>
                    {getPayment &&
                      getPayment.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {errors.payment_type}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="payment_reference">
                  <Form.Label>Payment Reference</Form.Label>
                  <Form.Control
                    type="text"
                    name="payment_reference"
                    value={values.payment_reference}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={
                      touched.payment_reference && !!errors.payment_reference
                    }
                    placeholder="Payment Reference"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.payment_reference}
                  </Form.Control.Feedback>
                </Form.Group>

                <Button
                  className="mr-2"
                  variant="secondary"
                  onClick={closeModal}
                >
                  Close
                </Button>
                <Button
                  className=" btn btn-info mr-1"
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span>
                      <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="false"
                      />
                      <span className="visually-hidden">Sending...</span>
                    </span>
                  ) : (
                    t("register")
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};

export default AddExpense;
